import { useEffect, useState } from "react";
import { Archive, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { FollowupSummary } from "@/components/followups/FollowupSummary";
import { useAuth } from "@/context/AuthContext";
import { SOURCE_SHORT, fmtDate, fmtDateTime } from "@/lib/constants";
import { BrandTag, LoadingBlock, SeverityChip, Spinner, StatusChip } from "@/components/common";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const EDIT = ["status", "compensation_type", "coupon_code", "coupon_status", "remarks", "assigned_to", "category", "severity", "sentiment"];

const Field = ({ label, children, locked }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}{locked && <Lock className="h-3 w-3" title="Edited by hand — uploads won't change it" />}</div>
    {children}
  </div>
);

const Info = ({ label, value }) => (
  <div><div className="text-xs text-slate-400">{label}</div><div className="text-sm font-medium text-slate-800 break-words">{value || "—"}</div></div>
);

const Pick = ({ value, options, onChange, disabled, testid }) => (
  <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger data-testid={testid} className="h-11 rounded-xl"><SelectValue placeholder="Choose..." /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

export const RecordPanel = ({ id, onClose, onSaved }) => {
  const { meta } = useApp();
  const { can } = useAuth();
  const [rec, setRec] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const editable = can("admin", "manager");

  useEffect(() => {
    if (!id) { setRec(null); return; }
    api.get(`/records/${id}`).then(({ data }) => {
      setRec(data);
      setForm(Object.fromEntries(EDIT.map((k) => [k, data[k] || ""])));
    }).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const changed = Object.fromEntries(EDIT.filter((k) => form[k] !== (rec[k] || "")).map((k) => [k, form[k]]));
      if (!Object.keys(changed).length) { toast.info("Nothing changed"); return; }
      await api.patch(`/records/${id}`, changed);
      toast.success("Saved. The brand sheet will update shortly.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const suggest = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/records/${id}/ai-suggest`);
      setForm((f) => ({ ...f, ...data }));
      toast.success("Suggestion filled in — check it and press Save");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAiBusy(false);
    }
  };
  const archive = async () => {
    if (!window.confirm("Archive this record? It will be hidden from lists and dashboards, but not deleted.")) return;
    await api.post(`/records/${id}/archive`);
    toast.success("Record archived");
    onSaved();
    onClose();
  };
  const locked = rec?.manual_locked || [];

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="record-panel">
        <SheetHeader className="mb-4 text-left">
          <SheetTitle className="font-display text-xl">Feedback details</SheetTitle>
          <SheetDescription>{rec ? `${rec.outlet} · ${fmtDate(rec.date)}` : "Loading..."}</SheetDescription>
        </SheetHeader>
        {!rec || !meta ? <LoadingBlock rows={6} /> : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2"><BrandTag brand={rec.brand} /><StatusChip status={rec.status} /><SeverityChip severity={rec.severity} /></div>
            <blockquote data-testid="record-text" className="rounded-xl border-l-4 border-slate-300 bg-slate-50 p-4 text-base text-slate-800">
              {rec.text || <span className="text-slate-400">No written feedback</span>}
            </blockquote>
            <div className="grid grid-cols-2 gap-4">
              <Info label="Customer" value={rec.customer_name} /><Info label="Phone" value={rec.customer_phone} />
              <Info label="Source" value={`${rec.source_type} · ${SOURCE_SHORT[rec.source_type]}`} /><Info label="Platform" value={rec.platform} />
              <Info label="Order / Review ID" value={rec.ref_id} /><Info label="Rating" value={rec.rating} />
              <Info label="Team" value={rec.department === "BOH" ? "BOH (Kitchen)" : rec.department === "FOH" ? "FOH (Service)" : ""} /><Info label="Requested refund" value={rec.requested_refund != null ? `₹${rec.requested_refund}` : ""} /><Info label="Refund amount" value={rec.refund_amount != null ? `₹${rec.refund_amount}` : ""} />
              <Info label="Items" value={rec.items} /><Info label="Resolved on" value={fmtDate(rec.resolved_date)} />
              <Info label="From file" value={rec.source_file} /><Info label="Added" value={fmtDateTime(rec.uploaded_at)} />
            </div>
            <FollowupSummary rec={rec} />
            <div className="space-y-4 rounded-2xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-800">Update</h4>
                {editable && <Button size="sm" variant="ghost" onClick={suggest} disabled={aiBusy} data-testid="ai-suggest-button">{aiBusy ? <Spinner /> : <Sparkles className="mr-1 h-4 w-4 text-violet-500" />}Suggest category</Button>}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Status"><Pick testid="edit-status" value={form.status} options={meta.statuses} onChange={set("status")} disabled={!editable} /></Field>
                <Field label="Assigned to" locked={locked.includes("assigned_to")}>
                  <Pick testid="edit-assigned-to" value={form.assigned_to} options={[...new Set([...(meta.team || []), ...(form.assigned_to ? [form.assigned_to] : [])])]} onChange={set("assigned_to")} disabled={!editable} />
                </Field>
                <Field label="Category" locked={locked.includes("category")}><Pick testid="edit-category" value={form.category} options={meta.categories} onChange={set("category")} disabled={!editable} /></Field>
                <Field label="Severity" locked={locked.includes("severity")}><Pick testid="edit-severity" value={form.severity} options={meta.severities} onChange={set("severity")} disabled={!editable} /></Field>
                <Field label="Sentiment" locked={locked.includes("sentiment")}><Pick testid="edit-sentiment" value={form.sentiment} options={meta.sentiments} onChange={set("sentiment")} disabled={!editable} /></Field>
                <Field label="Compensation"><Pick testid="edit-compensation" value={form.compensation_type} options={meta.compensation_types} onChange={set("compensation_type")} disabled={!editable} /></Field>
                <Field label="Coupon / voucher code"><Input data-testid="edit-coupon-code" className="h-11 rounded-xl" value={form.coupon_code} onChange={(e) => set("coupon_code")(e.target.value)} disabled={!editable} /></Field>
                <Field label="Coupon status"><Pick testid="edit-coupon-status" value={form.coupon_status} options={meta.coupon_statuses} onChange={set("coupon_status")} disabled={!editable} /></Field>
              </div>
              <Field label="Action taken / remarks"><Textarea data-testid="edit-remarks" rows={3} className="rounded-xl" value={form.remarks} onChange={(e) => set("remarks")(e.target.value)} disabled={!editable} /></Field>
              {editable && (
                <div className="flex gap-2">
                  <Button className="h-11 flex-1 rounded-xl text-base" onClick={save} disabled={busy} data-testid="record-save-button">{busy ? <Spinner /> : "Save changes"}</Button>
                  {can("admin") && <Button variant="outline" className="h-11 rounded-xl" onClick={archive} data-testid="record-archive-button"><Archive className="h-4 w-4" /></Button>}
                </div>
              )}
            </div>
            <div>
              <h4 className="mb-2 font-semibold text-slate-800">Change history</h4>
              {rec.history?.length ? (
                <ul className="space-y-2" data-testid="record-history">
                  {rec.history.map((h) => (
                    <li key={h.id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                      <span className="font-medium">{meta.labels[h.field] || h.field}</span>: <span className="text-slate-400 line-through">{h.old || "empty"}</span> → <span className="font-medium">{h.new}</span>
                      <div className="text-xs text-slate-400">{h.by} · {fmtDateTime(h.at)} · {h.source.split(":")[0]}</div>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-slate-400">No changes yet.</p>}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
